import * as React from "react"

import { cn } from "@/core/helpers/misc/GeneralUtils"

type KbdProps = React.ComponentPropsWithoutRef<"kbd"> & {
  /**
   * Optional list of key names to render as a shortcut label.
   * Mirrors the old Kbd API while keeping the shadcn visual shell.
   */
  keys?: string[]
  uppercase?: boolean
  /** Size variant - 'sm' for smaller version */
  size?: 'default' | 'sm'
}

function getKeySymbol(key: string): string {
  const isMac =
    typeof navigator !== "undefined" &&
    /mac|iphone|ipad|ipod/i.test((navigator as any).platform ?? "")

  switch (key.toLowerCase()) {
    case "meta":
      return isMac ? "⌘" : "Ctrl"
    case "ctrl":
      return "Ctrl"
    case "alt":
      return isMac ? "⌥" : "Alt"
    case "shift":
      return "⇧"
    case "tab":
      return "↹"
    case "enter":
      return "↵"
    case "backspace":
      return "⌫"
    case "delete":
    case "del":
      return "Del"
    case "escape":
    case "esc":
      return "Esc"
    case "arrowup":
    case "up":
      return "↑"
    case "arrowdown":
    case "down":
      return "↓"
    case "arrowleft":
    case "left":
      return "←"
    case "arrowright":
    case "right":
      return "→"
    default:
      return key.toUpperCase()
  }
}

// NOTE: Extends shadcn's Kbd to preserve the old keybinding UX (`keys` prop)
// while keeping the new visual shell and tooltip integration.
const Kbd = React.forwardRef<HTMLElement, KbdProps>(
  ({ className, keys, uppercase = false, size = 'default', children, ...props }, ref) => {
    const hasKeys = Array.isArray(keys) && keys.length > 0
    const label = hasKeys ? keys.map((k) => getKeySymbol(k)).join(" ") : null

    return (
      <kbd
        ref={ref as any}
        data-slot="kbd"
        className={cn(
          "shadcn-label shadcn-kbd bg-muted text-muted-foreground pointer-events-none inline-flex w-fit select-none items-center justify-center gap-1 rounded-sm font-sans font-medium",
          "[&_svg:not([class*='size-'])]:size-3",
          "[[data-slot=tooltip-content]_&]:bg-background/20 [[data-slot=tooltip-content]_&]:text-background dark:[[data-slot=tooltip-content]_&]:bg-background/10",
          size === 'sm' ? "h-4 min-w-4 px-0.5 text-[10px]" : "h-5 min-w-5 px-1 text-xs",
          uppercase && "uppercase tracking-wide",
          className
        )}
        {...props}
      >
        {label ?? children}
      </kbd>
    )
  }
)
Kbd.displayName = "Kbd"

function KbdGroup({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <kbd
      data-slot="kbd-group"
      className={cn("shadcn-kbdgroup inline-flex items-center gap-1", className)}
      {...props}
    />
  )
}

export { Kbd, KbdGroup }
